import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import type { AuthenticatedUser } from '../lib/auth/demoAuth';
import { WARDYA_DOCS_URL } from '../lib/portal';
import {
  Menu,
  X,
  User,
  Code,
  BookOpen,
  FileText,
  Webhook,
  Key,
  LogOut,
  Rocket
} from 'lucide-react';

interface LandingHeaderProps {
  onGetStarted?: () => void;
  onSignIn?: () => void;
  onSignOut?: () => void;
  onNavigate?: (section: string) => void;
  user?: AuthenticatedUser | null;
  userRole?: string | null;
}

export function LandingHeader({ 
  onGetStarted, 
  onSignIn: _onSignIn = onGetStarted, 
  onSignOut,
  onNavigate,
  user,
  userRole: _userRole 
}: LandingHeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  // Navigation handler
  const handleNavigation = (href: string) => {
    if (href.startsWith('/')) {
      navigate(href);
    } else if (href.startsWith('#')) {
      const sectionId = href.substring(1);
      if (onNavigate) {
        onNavigate(sectionId);
      } else {
        // Fallback to scroll behavior
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    } else {
      // External links
      window.open(href, '_blank', 'noopener,noreferrer');
    }
  };

  const navigationItems = [
    { name: 'Quickstart', href: '/developers/quickstart', icon: Rocket },
    { name: 'Docs Hub', href: '/developers/docs', icon: BookOpen },
    { name: 'API Reference', href: '/developers/api-reference', icon: Code },
    { name: 'Webhooks', href: '/developers/webhooks', icon: Webhook },
    { name: 'SDKs', href: '/developers/sdks', icon: BookOpen },
    { name: 'Sandbox', href: '/developers/sandbox', icon: FileText },
    { name: 'Security', href: '/developers/security', icon: Key },
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 ${scrolled
        ? 'bg-white/95 backdrop-blur-xl shadow-lg border-b border-gray-200/50'
        : 'bg-white/90 backdrop-blur-sm border-b border-gray-100/50'
      }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between gap-4">

          {/* Logo Section */}
          <div className="flex items-center min-w-0 flex-shrink-0">
            <div className="h-10 w-10 sm:h-12 sm:w-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer flex-shrink-0"
              onClick={() => navigate('/')}>
              <Code className="h-5 w-5 sm:h-6 sm:w-6 text-white group-hover:scale-110 transition-transform" />
            </div>

            <div className="ml-3 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="min-w-0">
                  <h1 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent whitespace-nowrap cursor-pointer"
                    onClick={() => navigate('/')}>
                    Wardya Developer
                  </h1>
                  <p className="text-xs text-gray-500 font-medium whitespace-nowrap hidden sm:block">
                    Developer Portal & APIs
                  </p>
                  <p className="text-xs text-gray-500 font-medium whitespace-nowrap sm:hidden">
                    Developer Portal
                  </p>
                </div>

                <div className="flex-shrink-0 hidden sm:block">
                  <Badge className="bg-gradient-to-r from-blue-500 to-purple-500 text-white border-0 px-2 py-1 text-xs animate-pulse whitespace-nowrap">
                    v2.1.0
                  </Badge>
                </div>
              </div>

              <div className="flex-shrink-0 sm:hidden mt-1">
                <Badge className="bg-gradient-to-r from-blue-500 to-purple-500 text-white border-0 px-2 py-1 text-xs animate-pulse whitespace-nowrap">
                  v2.1.0
                </Badge>
              </div>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navigationItems.map((item) => (
              <Button
                key={item.name}
                variant="ghost"
                onClick={() => handleNavigation(item.href)}
                className={`bg-transparent hover:bg-gray-50 font-medium px-3 py-2 rounded-lg transition-all duration-200 text-sm ${
                  location.pathname === item.href 
                    ? 'text-blue-600 bg-blue-50' 
                    : 'text-gray-700 hover:text-gray-900'
                }`}
              >
                <item.icon className="h-4 w-4 mr-2" />
                {item.name}
              </Button>
            ))}
          </nav>

          {/* Action Buttons */}
          <div className="flex items-center gap-3">
            <Button
              onClick={() => window.open(WARDYA_DOCS_URL, '_blank', 'noopener,noreferrer')}
              variant="ghost"
              className="hidden xl:flex items-center gap-2 text-gray-700"
            >
              <BookOpen className="h-4 w-4" />
              Main Docs
            </Button>

            {/* User Authentication */}
            {user ? (
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => navigate('/dashboard')}
                  variant="outline"
                  className="hidden sm:flex items-center gap-2 border-blue-200 text-blue-700 hover:bg-blue-50 hover:border-blue-300"
                >
                  <Key className="h-4 w-4" />
                  Dashboard
                </Button>
                <Button
                  onClick={() => navigate('/account')}
                  variant="ghost"
                  className="hidden sm:flex items-center gap-2 text-gray-700"
                >
                  <User className="h-4 w-4" />
                  {user.name || 'Account'}
                </Button>
                <Button
                  onClick={onSignOut}
                  variant="outline"
                  size="sm"
                  className="border-gray-300 hover:border-gray-400"
                  title="Sign out"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Button
                onClick={() => navigate('/login')}
                className="bg-blue-900 hover:bg-blue-800 text-white flex items-center gap-2"
              >
                <User className="h-4 w-4" />
                <span>Sign in</span>
              </Button>
            )}

            {/* Mobile Menu Toggle */}
            <Button
              variant="outline"
              size="sm"
              className="lg:hidden border-gray-300 hover:border-gray-400"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden fixed inset-x-0 top-[72px] bottom-0 z-50 bg-white/98 backdrop-blur-xl overflow-y-auto">
            <div className="container mx-auto px-6 py-6">
              <div className="grid grid-cols-1 gap-3">
                {navigationItems.map((item) => (
                  <Button
                    key={item.name}
                    onClick={() => {
                      handleNavigation(item.href);
                      setIsMenuOpen(false);
                    }}
                    variant="ghost"
                    className={`justify-start text-left hover:bg-gray-100 transition-colors duration-200 h-auto py-3 ${
                      location.pathname === item.href 
                        ? 'text-blue-600 bg-blue-50' 
                        : 'text-gray-700'
                    }`}
                  >
                    <item.icon className="h-5 w-5 mr-3" />
                    <span className="text-base font-medium">{item.name}</span>
                  </Button>
                ))}

                <div className="border-t border-gray-200 my-4"></div>

                {user ? (
                  <>
                    <Button
                      onClick={() => {
                        navigate('/dashboard');
                        setIsMenuOpen(false);
                      }}
                      variant="outline"
                      className="w-full border-2 border-blue-200 text-blue-700 hover:bg-blue-50 h-auto py-3 text-base font-medium"
                    >
                      <Key className="h-5 w-5 mr-2" />
                      Dashboard
                    </Button>
                    <Button
                      onClick={() => {
                        navigate('/account');
                        setIsMenuOpen(false);
                      }}
                      variant="outline"
                      className="w-full h-auto py-3 text-base font-medium"
                    >
                      <User className="h-5 w-5 mr-2" />
                      Account
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={() => {
                      navigate('/login');
                      setIsMenuOpen(false);
                    }}
                    className="w-full bg-blue-900 hover:bg-blue-800 text-white h-auto py-3 text-base font-medium"
                  >
                    <User className="h-5 w-5 mr-2" />
                    Sign in
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}